import type { ApiError } from "@khazzin/api-client";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

/**
 * Paging a forward-only API backwards.
 *
 * Every list in this system is cursor-paginated (`10/01` §8), and a cursor
 * points one way: the server can say "here are fifty rows and the token for the
 * next fifty" and cannot say what came before. That is the right trade for a
 * table over hundreds of thousands of rows — an offset scan of page 400 is a
 * table scan — but a person on page three still expects a Previous button.
 *
 * So the client keeps the stack. Each Next pushes the cursor it just used;
 * Previous pops. The stack is exactly as long as the number of pages somebody
 * has walked, which is the only history that can exist without the server
 * having one.
 *
 * **`placeholderData` keeps the old page on screen while the new one loads.**
 * Without it a table empties to a skeleton and refills on every press, which at
 * thirty rows is a flash of the whole screen. With it the rows stay, the footer
 * updates, and a page already in the cache — which is every page somebody has
 * been on — renders with no request at all.
 *
 * **A stale cursor is tolerated, not prevented** (`10/01` §9). Rows move; a
 * token can point at a row that has been deleted. The server answers with what
 * it can and the table renders it. Detecting and repairing that would mean
 * holding a snapshot, which is the offset pagination this exists to avoid.
 */

/** One page, as every list endpoint in this API returns it. */
export interface Page<Row> {
	items: Row[];
	/** The token for the next page, or null on the last one. */
	nextCursor: string | null;
	/** Some endpoints know the total; most do not, and the footer adapts. */
	total?: number;
}

/**
 * What the table needs to render a footer and move between pages.
 *
 * `P` is the whole page as the endpoint returns it, so a list whose response
 * carries more than rows — tab counts, a selection total — reaches them
 * through `page` without a second request that could disagree with the first.
 */
export interface CursorPagination<Row, P extends Page<Row> = Page<Row>> {
	page: P | undefined;
	status: "loading" | "error" | "success";
	error: ApiError | Error | null;
	refetch: () => void;
	limit: number;
	/** 1-based index of the first row on this page, for the footer. */
	showingFrom: number;
	/** 1-based index of the last row on this page. */
	showingTo: number;
	total: number | undefined;
	hasNext: boolean;
	hasPrevious: boolean;
	next: () => void;
	previous: () => void;
	/** Back to the first page. Called when a filter or a sort changes. */
	reset: () => void;
	/** The 1-based page number: the stack depth plus one. */
	pageNumber: number;
	/**
	 * Back to a page already walked. Forward is not offered and cannot be:
	 * reaching page 400 needs its cursor, and the only way to have one is to
	 * have been there.
	 */
	goTo: (page: number) => void;
}

/**
 * The largest page the client will ask for.
 *
 * `14/03` §3 caps a request at 100 and the server enforces its own ceiling. The
 * client keeps its own because the reason is different: a hundred rows is
 * already more than a screen, and asking for more spends bandwidth on rows
 * nobody scrolls to on a warehouse connection.
 */
export const MAX_LIMIT = 100;

/** The default page size: enough to fill a screen and scroll a little. */
export const DEFAULT_LIMIT = 50;

export interface CursorPaginationOptions {
	/** Distinguishes this list's cache from every other. Include the filters. */
	queryKey: readonly unknown[];
	limit?: number;
	enabled?: boolean;
	/**
	 * Raises this list's own ceiling above [MAX_LIMIT].
	 *
	 * One list has one: the Orders page, whose rows-per-page control offers 200
	 * (`07/07` §3.5). Every other list is better off refusing to fetch rows
	 * nobody scrolls to on a warehouse connection.
	 */
	maxLimit?: number;
}

/** The cursors walked to reach the current page of one list. */
export interface CursorStack {
	/** The cursor of the page on screen; null on page one. */
	cursor: string | null;
	/** How many pages back page one is. */
	depth: number;
	push: (token: string) => void;
	pop: () => void;
	reset: () => void;
	/** Back to a page already walked (1-based). */
	goTo: (page: number) => void;
}

const FIRST_PAGE: (string | null)[] = [];

/**
 * The cursors used to reach each page, **held against the list they belong
 * to** (ADJ-M03-6).
 *
 * `key` names the list: its filters, its search, its sort. When it changes the
 * stack is empty in the same render, so a new filter is asked for from page
 * one. Held separately, a filter changed on page three sent page three's
 * cursor with the new filter, which the server rejects or answers with rows
 * from the middle of a different list (Items, Movements, Lots…). Resetting in
 * an effect would still send that one wrong request first.
 */
export function useCursorStack(key: string): CursorStack {
	const [held, setHeld] = useState<{ key: string; stack: (string | null)[] }>(() => ({
		key,
		stack: FIRST_PAGE,
	}));
	const stack = held.key === key ? held.stack : FIRST_PAGE;
	const change = useCallback(
		(next: (current: (string | null)[]) => (string | null)[]) =>
			setHeld((current) => ({
				key,
				stack: next(current.key === key ? current.stack : FIRST_PAGE),
			})),
		[key],
	);
	const push = useCallback((token: string) => change((current) => [...current, token]), [change]);
	const pop = useCallback(() => change((current) => current.slice(0, -1)), [change]);
	const reset = useCallback(() => change(() => FIRST_PAGE), [change]);
	// Only backwards: the stack holds the cursor used to reach each page, so
	// page n is reachable exactly while it is in it.
	const goTo = useCallback(
		(page: number) => change((current) => current.slice(0, Math.max(0, page - 1))),
		[change],
	);
	return {
		cursor: stack.length === 0 ? null : (stack[stack.length - 1] ?? null),
		depth: stack.length,
		push,
		pop,
		reset,
		goTo,
	};
}

export function useCursorPagination<Row, P extends Page<Row> = Page<Row>>(
	fetchPage: (cursor: string | null, limit: number, signal: AbortSignal) => Promise<P>,
	{
		queryKey,
		limit = DEFAULT_LIMIT,
		enabled = true,
		maxLimit = MAX_LIMIT,
	}: CursorPaginationOptions,
): CursorPagination<Row, P> {
	const size = Math.min(Math.max(1, limit), maxLimit);

	// The cursors used to reach each page before this one, emptied in the same
	// render whenever the list's key (its filters) changes (ADJ-M03-3, then
	// ADJ-M03-6's shared `useCursorStack`): a cursor is bound to the filters that
	// produced it, and a filter can change without the page calling reset.
	const stack = useCursorStack(JSON.stringify([...queryKey, size]));
	const cursor = stack.cursor;

	const query = useQuery({
		// The cursor is part of the key, so each page is cached separately and
		// walking back is free.
		queryKey: [...queryKey, "page", cursor, size],
		queryFn: ({ signal }) => fetchPage(cursor, size, signal),
		enabled,
		// The previous page's rows stay on screen while the next one loads.
		placeholderData: (previous) => previous,
	});

	const { push } = stack;
	const next = useCallback(() => {
		const token = query.data?.nextCursor;
		if (token === null || token === undefined) {
			return;
		}
		push(token);
	}, [query.data?.nextCursor, push]);

	const items = query.data?.items ?? [];
	// The page number is the stack depth, which is the only thing the client can
	// know: the server never said how far in this cursor is.
	const showingFrom = items.length === 0 ? 0 : stack.depth * size + 1;

	return {
		page: query.data,
		status: query.isError ? "error" : query.isLoading ? "loading" : "success",
		error: query.error,
		refetch: () => void query.refetch(),
		limit: size,
		showingFrom,
		showingTo: items.length === 0 ? 0 : showingFrom + items.length - 1,
		total: query.data?.total,
		hasNext: (query.data?.nextCursor ?? null) !== null,
		hasPrevious: stack.depth > 0,
		next,
		previous: stack.pop,
		reset: stack.reset,
		pageNumber: stack.depth + 1,
		goTo: stack.goTo,
	};
}
